/**
 * @fileoverview PostHog session-replay masking policy (security audit LOG-4).
 *
 * Replay used to run with `maskAllInputs: false`, so everything typed into the
 * app — prompts, sign-in emails, BYOK provider keys in settings — was recorded
 * verbatim, and every text node went out as-is. Inputs are now masked
 * wholesale (rrweb replaces their value with asterisks), and text nodes pass
 * through `maskReplayText`, which redacts anything shaped like an email
 * address wherever it appears (member lists, invitations, billing, account
 * settings) while leaving the rest of the UI readable so replays stay useful.
 */

import type { SessionRecordingOptions } from 'posthog-js'

export const REPLAY_EMAIL_MASK = '[email]'

// Deliberately loose: replay text is not validated input, it only has to
// catch anything that reads as an address. Requires a dotted domain so
// @mentions (`@alice`) stay visible.
const EMAIL_PATTERN = /[\w.+%-]+@[\w-]+(?:\.[\w-]+)+/gi

/** Redacts email addresses in a replay text node; returns the input untouched when there are none. */
export function maskReplayText(text: string): string {
  if (!text || !text.includes('@')) return text
  return text.replace(EMAIL_PATTERN, REPLAY_EMAIL_MASK)
}

/**
 * The masking slice of PostHog's `session_recording` config. `maskTextSelector`
 * is `*` because rrweb only runs `maskTextFn` over text nodes that match the
 * selector, and the function itself decides what to redact.
 */
export const REPLAY_MASKING_CONFIG = {
  maskAllInputs: true,
  maskTextSelector: '*',
  maskTextFn: maskReplayText,
} satisfies Pick<SessionRecordingOptions, 'maskAllInputs' | 'maskTextSelector' | 'maskTextFn'>
